<script lang="ts">
  import { api } from '$shared/api';
  import { getUser, fetchCurrentUser } from '$lib/auth.svelte';
  import Avatar from '$shared/Avatar.svelte';
  import LoadingSkeleton from '$shared/LoadingSkeleton.svelte';
  import type { Achievement } from '$types/index';
  import { connectAppleMusic, disconnectAppleMusic as disconnectAppleMusicApi } from '$lib/appleMusic.svelte';

  const user = $derived(getUser());
  let achievements = $state<Achievement[]>([]);
  let loading = $state(true);

  // Email verification
  let resending = $state(false);
  let resendMsg = $state('');

  // OAuth connections
  let connectingSpotify = $state(false);
  let connectingSC = $state(false);
  let disconnecting = $state('');
  let oauthMsg = $state('');
  let connectingApple = $state(false);
  let disconnectingApple = $state(false);

  // Surface Spotify connect errors set by initAuth after the OAuth redirect.
  const spotifyConnectErr = sessionStorage.getItem('spotify_connect_error');
  if (spotifyConnectErr) {
    oauthMsg = spotifyConnectErr;
    sessionStorage.removeItem('spotify_connect_error');
  }

  // Surface a Spotify-just-connected success message set by initAuth after the connect
  // OAuth redirect (mirrors the error read above; AccountHub is the "return to /account"
  // target for a connect started from here, distinct from Onboarding's own consumption
  // of the same key to auto-load genre suggestions).
  if (sessionStorage.getItem('music_just_connected') === 'spotify') {
    oauthMsg = 'Spotify connected!';
    sessionStorage.removeItem('music_just_connected');
  }

  $effect(() => {
    if (!user) return;
    api
      .get<{ achievements: Achievement[] }>(`/users/${user.id}/achievements`)
      .then((d) => {
        achievements = d.achievements ?? [];
        loading = false;
      })
      .catch((e: unknown) => {
        console.error('Failed to load achievements:', e);
        loading = false;
      });
  });

  const earnedCount = $derived(achievements.filter((a) => a.earned).length);

  const navItems = [
    { href: '#/account/profile', label: 'Edit Profile', desc: 'Profile image, username' },
    { href: '#/account/security', label: 'Security', desc: 'Password and email' },
    { href: '#/account/notifications', label: 'Notifications', desc: 'Email alerts, game sounds' },
    { href: '#/account/stats', label: 'Stats', desc: 'Points, achievements, streaks' },
    { href: '#/catalog', label: 'My Catalog', desc: 'Your songs in the duel catalog' },
    { href: '#/account/saved', label: 'Saved', desc: 'Your saved tracks (duels + catalog)' },
    { href: '#/account/blocked-users', label: 'Blocked Users', desc: 'Manage blocked accounts' },
    { href: '#/account/invite', label: 'Invite Friends', desc: 'Share invite codes' },
  ];

  async function resendVerification() {
    resending = true;
    resendMsg = '';
    try {
      await api.post('/auth/resend-verification', {});
      resendMsg = 'Verification email sent!';
    } catch (e) {
      console.error('Resend verification failed:', e);
      resendMsg = 'Could not send email';
    } finally {
      resending = false;
    }
  }

  async function connectSpotify() {
    connectingSpotify = true;
    try {
      const { url, state } = await api.get<{ url: string; state: string }>('/auth/spotify/url');
      if (!url) {
        oauthMsg = 'Spotify is not configured';
        connectingSpotify = false;
        return;
      }
      if (state) sessionStorage.setItem('spotify_connect_state', state);
      sessionStorage.setItem('spotify_connect_return', '/account');
      window.location.href = url;
    } catch (e) {
      console.error('Spotify connect failed:', e);
      oauthMsg = 'Could not get Spotify URL';
      connectingSpotify = false;
    }
  }

  async function connectSoundCloud() {
    connectingSC = true;
    try {
      const { url } = await api.get<{ url: string }>('/auth/soundcloud/url');
      window.location.href = url;
    } catch (e) {
      console.error('SoundCloud connect failed:', e);
      oauthMsg = 'Could not get SoundCloud URL';
      connectingSC = false;
    }
  }

  async function disconnect(provider: 'spotify' | 'soundcloud') {
    disconnecting = provider;
    try {
      await api.post(`/auth/${provider}/disconnect`, {});
      await fetchCurrentUser();
      oauthMsg = `${provider === 'spotify' ? 'Spotify' : 'SoundCloud'} disconnected`;
    } catch (e) {
      console.error('OAuth disconnect failed:', e);
      oauthMsg = 'Disconnect failed';
    } finally {
      disconnecting = '';
    }
  }

  async function handleConnectAppleMusic() {
    connectingApple = true;
    try {
      await connectAppleMusic();
      await fetchCurrentUser();
      oauthMsg = 'Apple Music connected';
    } catch (e) {
      console.error('Apple Music connect failed:', e);
      oauthMsg = (e as Error).message || 'Could not connect Apple Music';
    } finally {
      connectingApple = false;
    }
  }

  async function handleDisconnectAppleMusic() {
    disconnectingApple = true;
    try {
      await disconnectAppleMusicApi();
      await fetchCurrentUser();
      oauthMsg = 'Apple Music disconnected';
    } catch (e) {
      console.error('Apple Music disconnect failed:', e);
      oauthMsg = 'Disconnect failed';
    } finally {
      disconnectingApple = false;
    }
  }
</script>

<div>
  {#if user}
    <div class="profile-row">
      <Avatar username={user.username} avatarUrl={user.avatar_url} size="lg" />
      <div>
        <h1>{user.username}</h1>
        <p class="email">{user.email}</p>
      </div>
    </div>

    <!-- Email verification banner -->
    {#if user.email_verified === false}
      <div class="verify-banner">
        <span>Email not verified.</span>
        <button class="btn-link" onclick={resendVerification} disabled={resending}>
          {resending ? 'Sending...' : 'Resend verification email'}
        </button>
        {#if resendMsg}<span class="verify-msg">{resendMsg}</span>{/if}
      </div>
    {/if}
  {/if}

  <div class="grid-cards nav-grid">
    {#each navItems as item}
      <a href={item.href} class="card">
        <h3>{item.label}</h3>
        <p>{item.desc}</p>
      </a>
    {/each}
  </div>

  <!-- Connected Services -->
  {#if user}
    <h2 class="section-title">Connected Services</h2>
    {#if oauthMsg}<p class="oauth-msg">{oauthMsg}</p>{/if}
    <div class="services-grid">
      <div class="service-card">
        <span class="service-name">Spotify</span>
        {#if user.spotify_id}
          <span class="service-status connected">Connected</span>
          <button
            class="btn btn-outline btn-sm"
            onclick={() => disconnect('spotify')}
            disabled={disconnecting === 'spotify'}
          >
            {disconnecting === 'spotify' ? '...' : 'Disconnect'}
          </button>
        {:else}
          <button class="btn btn-primary btn-sm" onclick={connectSpotify} disabled={connectingSpotify}>
            {connectingSpotify ? 'Redirecting...' : 'Connect'}
          </button>
        {/if}
        <p class="service-desc">Builds your Music DNA from top artists</p>
      </div>
      <div class="service-card">
        <span class="service-name">SoundCloud</span>
        {#if user.soundcloud_user_id}
          <span class="service-status connected">Connected</span>
          <button
            class="btn btn-outline btn-sm"
            onclick={() => disconnect('soundcloud')}
            disabled={disconnecting === 'soundcloud'}
          >
            {disconnecting === 'soundcloud' ? '...' : 'Disconnect'}
          </button>
        {:else}
          <button class="btn btn-primary btn-sm" onclick={connectSoundCloud} disabled={connectingSC}>
            {connectingSC ? 'Redirecting...' : 'Connect'}
          </button>
        {/if}
        <p class="service-desc">Enhances Music DNA + enables SC repost</p>
      </div>
      <div class="service-card">
        <span class="service-name">Apple Music</span>
        {#if user.apple_music_connected_at}
          <span class="service-status connected">Connected</span>
          <button class="btn btn-outline btn-sm" onclick={handleDisconnectAppleMusic} disabled={disconnectingApple}>
            {disconnectingApple ? '...' : 'Disconnect'}
          </button>
        {:else}
          <button class="btn btn-primary btn-sm" onclick={handleConnectAppleMusic} disabled={connectingApple}>
            {connectingApple ? 'Connecting...' : 'Connect'}
          </button>
        {/if}
        <p class="service-desc">Builds your Music DNA from your Apple Music library</p>
      </div>
    </div>
  {/if}

  <h2 class="section-title">Achievements <span class="badge-count">({earnedCount}/{achievements.length})</span></h2>

  {#if loading}
    <LoadingSkeleton lines={3} />
  {:else}
    <div class="badge-grid">
      {#each achievements as badge}
        <div class="badge-item" class:earned={badge.earned} title="{badge.name}: {badge.description}">
          <span class="badge-icon">{badge.icon}</span>
          <span class="badge-name">{badge.name}</span>
        </div>
      {/each}
    </div>
  {/if}
</div>

<style>
  .profile-row {
    display: flex;
    align-items: center;
    gap: 1rem;
    margin-bottom: 1.5rem;
  }
  .profile-row h1 {
    margin: 0;
  }
  .email {
    color: var(--text-secondary);
    font-size: 0.875rem;
    margin: 0.25rem 0 0;
  }

  /* Email verification banner */
  .verify-banner {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    flex-wrap: wrap;
    padding: 0.75rem 1rem;
    margin-bottom: 1.5rem;
    background: rgba(255, 45, 85, 0.1);
    border: 1px solid rgba(255, 45, 85, 0.3);
    border-radius: var(--radius);
    font-size: 0.875rem;
    color: var(--text);
  }
  .btn-link {
    background: none;
    border: none;
    color: var(--cyan);
    cursor: pointer;
    text-decoration: underline;
    font-size: 0.875rem;
    padding: 0;
  }
  .btn-link:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  .verify-msg {
    font-size: 0.8rem;
    color: var(--text-muted);
  }

  /* Connected Services */
  .services-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
    gap: 0.75rem;
    margin-bottom: 2rem;
  }
  .service-card {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    padding: 1rem;
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: var(--radius);
  }
  .service-name {
    font-weight: 600;
    font-size: 0.95rem;
  }
  .service-status {
    font-size: 0.75rem;
  }
  .service-status.connected {
    color: #10b981;
  }
  .service-desc {
    font-size: 0.75rem;
    color: var(--text-muted);
    margin: 0;
  }
  .btn-sm {
    font-size: 0.8rem;
    padding: 0.3rem 0.75rem;
    width: fit-content;
  }
  .oauth-msg {
    font-size: 0.875rem;
    color: var(--cyan);
    margin-bottom: 0.75rem;
  }

  .nav-grid {
    margin-bottom: 2rem;
  }
  .nav-grid h3 {
    margin: 0;
    font-weight: 600;
  }
  .nav-grid p {
    margin: 0.25rem 0 0;
    font-size: 0.875rem;
    color: var(--text-secondary);
  }
  .section-title {
    font-size: 1.125rem;
    margin-bottom: 0.75rem;
  }
  .badge-count {
    font-size: 0.875rem;
    font-weight: 400;
    color: var(--text-muted);
  }
  .badge-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 0.5rem;
  }
  @media (min-width: 640px) {
    .badge-grid {
      grid-template-columns: repeat(5, 1fr);
    }
  }
  @media (min-width: 1024px) {
    .badge-grid {
      grid-template-columns: repeat(7, 1fr);
    }
  }
  .badge-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    padding: 0.5rem;
    border: 1px solid var(--border);
    border-radius: var(--radius-sm);
    background: var(--surface);
    opacity: 0.4;
    transition: opacity 0.15s;
  }
  .badge-item.earned {
    opacity: 1;
    border-color: rgba(0, 229, 255, 0.3);
  }
  .badge-icon {
    font-size: 1.5rem;
  }
  .badge-name {
    font-size: 0.65rem;
    line-height: 1.2;
    color: var(--text-secondary);
    margin-top: 0.25rem;
  }
</style>
